import { z } from "zod";
import type { PublicRegistrationInput } from "@/api/types/classRegistration";

/**
 * Public class-registration form schema — the react-hook-form +
 * zodResolver wiring convention already used by
 * features/contacts/ContactFormDialog.tsx / features/pipeline/
 * InquiryFormDialog.tsx, applied here because this form has the same
 * "many fields, real validation" shape as those two (unlike the simpler
 * plain-useState admin dialogs in features/memberships/ or
 * features/bookings/).
 *
 * `waiver_ack`/`disclosure_ack` must be explicitly checked and
 * `media_release` must be an explicit choice — this mirrors (but does not
 * replace) the server's own enforcement in
 * app/services/class_registration.py: the server 422s on the exact same
 * conditions regardless of what this schema does, per the brief's "don't
 * rely on the server 422 alone for UX" instruction.
 */
export const ClassRegistrationFormSchema = z.object({
  section_id: z.string().min(1, "Select a class to enroll in"),

  guardian_name: z.string().min(1, "Your name is required"),
  guardian_email: z
    .union([z.string().email("Enter a valid email"), z.literal("")])
    .optional(),
  guardian_phone: z.string().optional(),

  child_first_name: z.string().min(1, "Child's first name is required"),
  child_last_name: z.string().min(1, "Child's last name is required"),
  child_birthdate: z.string().min(1, "Child's birthdate is required"),

  // Plain non-empty strings rather than a literal-union schema — the
  // <select>/radio inputs only ever emit "", "direct", or "esa_tefa" (resp.
  // "", "granted", or "declined") in practice, so a `min(1)` presence check
  // is all the client-side UX needs; toPublicRegistrationInput's cast to
  // the strict PaymentChannel/MediaReleaseChoice types below is safe on
  // that basis, and the server independently re-validates both against
  // its own Literal/Enum fields regardless.
  payment_channel: z.string().min(1, "Choose how you'll pay"),

  waiver_ack: z.boolean().refine((v) => v === true, {
    message: "You must accept the liability waiver to enroll",
  }),
  disclosure_ack: z.boolean().refine((v) => v === true, {
    message: "You must acknowledge the statutory disclosure to enroll",
  }),
  media_release: z.string().min(1, "Select a media release option"),

  notes: z.string().optional(),

  // Honeypot — always rendered, always submitted, never filled by a real
  // guardian. See ClassRegistrationPage's hidden field for the a11y/visual
  // treatment.
  website: z.string().optional(),
});
export type ClassRegistrationFormValues = z.infer<typeof ClassRegistrationFormSchema>;

export const EMPTY_CLASS_REGISTRATION_FORM_VALUES: ClassRegistrationFormValues = {
  section_id: "",
  guardian_name: "",
  guardian_email: "",
  guardian_phone: "",
  child_first_name: "",
  child_last_name: "",
  child_birthdate: "",
  payment_channel: "",
  waiver_ack: false,
  disclosure_ack: false,
  media_release: "",
  notes: "",
  website: "",
};

/** Normalize form strings ("" for an untouched optional field) to the
 * API's nullable fields — same "blank to null" convention as
 * features/pipeline/inquiryForm.ts::toApiInput. */
export function toPublicRegistrationInput(values: ClassRegistrationFormValues): PublicRegistrationInput {
  const blankToNull = (v: string | undefined) => (v && v.trim() !== "" ? v.trim() : null);

  // The refine()s above guarantee these are non-empty by the time
  // handleSubmit's onValid callback runs; the `as` casts here only strip
  // the `| ""` the union needs for an unselected radio/select's initial
  // render state.
  return {
    section_id: values.section_id,
    guardian_name: values.guardian_name.trim(),
    guardian_email: blankToNull(values.guardian_email),
    guardian_phone: blankToNull(values.guardian_phone),
    child_first_name: values.child_first_name.trim(),
    child_last_name: values.child_last_name.trim(),
    child_birthdate: values.child_birthdate,
    payment_channel: values.payment_channel as PublicRegistrationInput["payment_channel"],
    waiver_ack: values.waiver_ack,
    disclosure_ack: values.disclosure_ack,
    media_release: values.media_release as PublicRegistrationInput["media_release"],
    notes: blankToNull(values.notes),
    website: "",
  };
}
